import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAccess } from "../../../access/hooks/useAccess";
import { useOperationalAccess } from "../../../access/hooks/useOperationalAccess";
import { ludDashboardPermissions } from "../../../access/modules/lud-dashboard";
import { getApplicationsOverviewAPI } from "../../../utils/api/applications";
import ActivateModal from "./ActivateModal";
import SubscribeModal from "./SubscribeModal";
import IntegrateModal from "./IntegrateModal";
import ManageModal from "./ManageModal";
import ActivateBranchesModal from "./ActivateBranchesModal";
import DeactivateModal from "./DeactivateModal";
import BranchAccountModal from "./BranchAccountModal";
import { applicationsText, errorMessage } from "./applicationsText";

/*
  The one implementation of the application workflows (subscribe, integrate,
  activate, deactivate, manage requests, admin review), shared by the
  Applications page and the Branches page. A page calls `open(type, app,
  { branchIds })` and renders `element` once; `branchIds` is the branch the
  flow was opened from (it starts selected; other branches of the store can be
  added in the flow). After any change `revision` increases and the page's
  `onChanged` runs, so every consumer reloads from the same backend data.
*/
export default function useApplicationFlows({ lang, storeId, role, onChanged, onGoToCompletion, autoLoad = true }) {
  const t = applicationsText(lang);
  const ar = lang !== "en";
  const { has } = useAccess();
  // Managing applications (subscribe, integrate, activate, deactivate, requests) is operational: it needs the permission AND an active
  // subscription. A `new` client does not hold the permission but sees the actions locked and explained, like an expired store.
  const operational = useOperationalAccess(storeId);
  const manage = operational.check(ludDashboardPermissions.applications.manage, { restaurantId: storeId });
  const canManage = manage.allowed;
  const lock = manage.locked ? { reason: manage.reason } : null;
  const canReview = has(ludDashboardPermissions.applications.review) && role === "admin";
  const token = localStorage.getItem("auth-token");

  const [overview, setOverview] = useState({ status: autoLoad ? "loading" : "idle", data: null, error: "" });
  const [modal, setModal] = useState(null); // { type, app, branchIds?, request?, reviewId? }
  const [toast, setToast] = useState(null);
  const [revision, setRevision] = useState(0);
  const latest = useRef("");
  const toastTimer = useRef(null);

  const appName = (app) => (ar ? app.nameAr : app.nameEn);

  // Only the most recent store's answer may be shown: switching stores drops
  // the old data at once and ignores late responses of the previous store.
  const load = useCallback(
    async (silent = false) => {
      if (!storeId || !token) return;
      latest.current = storeId;
      if (!silent) setOverview({ status: "loading", data: null, error: "" });
      try {
        const res = await getApplicationsOverviewAPI(token, storeId);
        if (latest.current !== storeId) return;
        setOverview({ status: "ready", data: res.data, error: "" });
      } catch (e) {
        if (latest.current !== storeId) return;
        setOverview({ status: "error", data: null, error: errorMessage(t, e) });
      }
    },
    [storeId, token, t],
  );

  useEffect(() => {
    setModal(null);
    if (autoLoad) load();
  }, [load, autoLoad]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const showToast = useCallback((message, type = "success") => {
    if (!message) return;
    clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), type === "error" ? 7000 : 4500);
  }, []);

  // After any change: reload (no flicker) and let the dashboard refresh the data
  // the other panels show (accounts, branches).
  const changed = useCallback(
    (message, type) => {
      showToast(message, type);
      setRevision((value) => value + 1);
      if (autoLoad) load(true);
      onChanged?.();
    },
    [autoLoad, load, onChanged, showToast],
  );

  const open = useCallback((type, app, extra = {}) => setModal({ type, app, ...extra }), []);
  const close = useCallback(() => setModal(null), []);
  const done = (message, type) => {
    setModal(null);
    changed(message, type);
  };

  const common = { storeId, t, token };
  const branchIds = modal?.branchIds || [];

  const element = (
    <>
      {modal?.type === "activate" && (
        <ActivateModal open appName={appName(modal.app)} t={t} onClose={close} onChoose={(choice) => open(choice, modal.app, { branchIds })} />
      )}
      {modal?.type === "subscribe" && (
        <SubscribeModal
          open
          {...common}
          app={modal.app}
          appName={appName(modal.app)}
          lang={lang}
          initialBranchIds={branchIds}
          onClose={close}
          onDone={done}
          onGoCompletion={() => {
            close();
            onGoToCompletion?.();
          }}
        />
      )}
      {modal?.type === "integrate" && (
        <IntegrateModal open {...common} app={modal.app} appName={appName(modal.app)} request={modal.request || null} initialBranchIds={branchIds} startStep={modal.startStep || 0} onClose={close} onDone={done} />
      )}
      {modal?.type === "activateBranches" && (
        <ActivateBranchesModal open {...common} app={modal.app} appName={appName(modal.app)} request={modal.request || null} initialBranchIds={branchIds} onClose={close} onDone={done} onEditInfo={(app) => open("integrate", app, { branchIds })} />
      )}
      {modal?.type === "deactivate" && (
        <DeactivateModal open {...common} app={modal.app} appName={appName(modal.app)} request={modal.request || null} scope={modal.scope || "branches"} initialBranchIds={branchIds} onClose={close} onDone={done} />
      )}
      {modal?.type === "mapping" && (
        <BranchAccountModal
          open
          {...common}
          app={modal.app}
          appName={appName(modal.app)}
          branch={modal.branch || (modal.request ? { id: modal.request.branchIds[0], name: modal.request.changes?.[0]?.name || "" } : null)}
          initial={modal.initial || {}}
          request={modal.request || null}
          onClose={close}
          onDone={done}
        />
      )}
      {modal?.type === "manage" && (
        <ManageModal
          open
          {...common}
          app={modal.app}
          appName={appName(modal.app)}
          lang={lang}
          canManage={canManage}
          canReview={canReview}
          reviewId={modal.reviewId || null}
          onClose={close}
          onChanged={changed}
          onIntegrate={(app) => open("integrate", app)}
          onAddBranches={(app) => open("activate", app)}
          onActivateBranches={(app) => open("activateBranches", app)}
          onDeactivate={(app, extra) => open("deactivate", app, extra)}
          onEditRequest={(app, request) => open({ integration: "integrate", activation: "activateBranches", deactivation: "deactivate", mapping: "mapping" }[request.type], app, { request })}
        />
      )}
      {toast && (
        <div className={`lud-applications-toast ${toast.type}`} role={toast.type === "error" ? "alert" : "status"} dir={ar ? "rtl" : "ltr"}>
          {toast.message}
        </div>
      )}
    </>
  );

  return { t, ar, token, canManage, lock, canReview, overview, load, revision, modal, open, close, changed, showToast, appName, element };
}
